import React, { useMemo, useRef, useState } from 'react';

// helpers
import moment from 'moment';
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { FormikProps } from 'formik';
import { generateUniqId } from 'helpers/commonHelpers';
import { submitMultipleWireTransfersValidationSchema } from 'validations/accounting/transfers';
import { FormValuesModel as SubmitBulkFormValuesModal } from 'components/ModalDialogs/TemplateModalDialogs/Transfers/SubmitTransactionsBulkModal';
import {
  FetchTransactionBalancesPayload,
  transfersAPI,
} from 'api/accounting/transfersAPI';
import {
  convertAmountFromBigIntToInt,
  convertAmountFromIntToBigInt,
} from 'helpers/accountsHelpers';

// components
import { Form, Col, Row } from '@ui';
import SubmitButton from 'components/Buttons/SubmitButton';
import SubmitTransactionsModal from './SubmitTransactionsModal';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import WireTransferGridForm, {
  FormValuesModel,
} from 'components/Forms/TemplateForms/Transfers/WireTransferGridForm';

const SubmitButtonGridSizes = { xl: 4, lg: 6, md: 8, sm: 24, xs: 24 };

const SubmitMultipleTransactionsForm = () => {
  const formRef = useRef<FormikProps<FormValuesModel>>(null);
  const { t } = useTranslation('common');
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const initialValues = useMemo<FormValuesModel>(() => {
    return {
      transactions: [
        {
          customId: generateUniqId(),
          from: null,
          fromFieldLabel: '',
          balance: null,
          purpose: '',
          amount: null,
          currencyId: null,
          currency: null,
          valueDate: moment(),
          fromReference: '',
          to: null,
          toFieldLabel: null,
          toReference: '',
          fee: null,
        },
      ],
    };
  }, []);

  const handleSubmit = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = async (
    form: FormikProps<FormValuesModel>,
    wasSubmitted?: boolean,
    values?: SubmitBulkFormValuesModal,
  ) => {
    setIsModalVisible(false);

    if (wasSubmitted && values) {
      if (
        values &&
        values.result?.failedTransactions &&
        values.result.failedTransactions.length
      ) {
        const failedTransactions = form.values.transactions.filter(
          (_, i) =>
            values.result &&
            values.result.failedTransactions.some(
              (f) => f.transactionIndex === i,
            ),
        );

        const formattedTransactions: FetchTransactionBalancesPayload =
          failedTransactions.map((e) => ({
            sequenceNumber: e.customId,
            fromAccountNumber: e.from as string,
            currencyId: e.currencyId as number,
            amount: e.amount ? convertAmountFromIntToBigInt(e.amount) : 0,
          }));

        const response = await transfersAPI.fetchBalancesForWireTransfers(
          formattedTransactions,
        );

        const updatedTransactions = failedTransactions.map((transaction) => {
          const updatedTransaction = response.find(
            (responseTransaction) =>
              responseTransaction.sequenceNumber === transaction.customId,
          );

          if (updatedTransaction) {
            return {
              ...transaction,
              fee: convertAmountFromBigIntToInt(
                updatedTransaction.fromAccountFeeAmount,
              ),
              balance: transaction.balance
                ? {
                    ...transaction.balance,
                    available: convertAmountFromBigIntToInt(
                      updatedTransaction.availableBalance,
                    ),
                  }
                : null,
            };
          }

          return transaction;
        });

        form.resetForm({
          errors: {},
          touched: {},
          isSubmitting: false,
          isValidating: false,
          status: undefined,
          submitCount: 0,
          values: {
            transactions: updatedTransactions,
          },
        });
      } else {
        form.resetForm({
          errors: {},
          touched: {},
          isSubmitting: false,
          isValidating: false,
          status: undefined,
          submitCount: 0,
          values: initialValues,
        });
      }
    }
  };

  return (
    <Form<FormValuesModel>
      innerRef={formRef}
      confirmExitWithoutSaving
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={submitMultipleWireTransfersValidationSchema}
      renderForm={(form) => (
        <>
          <WireTransferGridForm />
          <HideIfDisabledForm>
            <Row justify="end">
              <Col {...SubmitButtonGridSizes}>
                <StyledSubmitButton type="primary">
                  {t('submit', { ns: 'common' })}
                </StyledSubmitButton>
              </Col>
            </Row>
          </HideIfDisabledForm>
          <SubmitTransactionsModal
            isVisible={isModalVisible}
            closeCallback={(wasSubmitted, values) =>
              handleModalClose(form, wasSubmitted, values)
            }
          />
        </>
      )}
    />
  );
};

const StyledSubmitButton = styled(SubmitButton)`
  width: 100%;
  padding: 0px;
  min-width: 0px;
`;

export default SubmitMultipleTransactionsForm;
